import React, { useState, useEffect, memo } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackInteraction } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { useAvailableParticipationPartitionCadences } from './hooks';
import { Option, Combobox } from '../../providers/harmony-scope/lazy-enablers';
import { EPartitionCadence } from '../../ducks/filters/types-filters';

interface IProps {
  selectedParticipation: string;
  selectedPartitionGroup: string;
  selectedCadence: EPartitionCadence;
  onChange: (partitionCadence: EPartitionCadence) => void;
}

const componentName = 'Participation Cadence Picker Filters Panel';
export const ComponentFilterParticipationCadence = memo((props: IProps) => {
  // Props Accessors
  const {
    selectedParticipation: selectedParticipationProp,
    selectedPartitionGroup: selectedParticipationPartitionGroupProp,
    selectedCadence: selectedCadenceProp,
    onChange,
  } = props;

  // Hooks
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const availableCadences = useAvailableParticipationPartitionCadences(selectedParticipationProp, selectedParticipationPartitionGroupProp);
  const [selectedCadence, setSelectedCadence] = useState(selectedCadenceProp);

  // Effects
  useEffect(() => {
    setSelectedCadence(selectedCadenceProp);
  }, [selectedCadenceProp]);

  useEffect(() => {
    onChange(selectedCadence);
  }, [onChange, selectedCadence]);

  useEffect(() => {
    // When the availableCadances change, if the selected cadence is not in the list, select the first one
    if (
      !availableCadences.find((cadence) => cadence.partitionCadence === selectedCadence) &&
      availableCadences.length > 0 &&
      availableCadences[0].partitionCadence
    ) {
      setSelectedCadence(availableCadences[0].partitionCadence);
    }
  }, [availableCadences, selectedCadence]);

  // Handlers
  const cadenceChanged = (event: any) => {
    if (!event.target.value) return;
    const newPartitionCadence = event.target.value;
    trackInteraction('Selected participation cadence', pageName, componentName, {
      selectedParticipationCadence: newPartitionCadence,
    });
    setSelectedCadence(newPartitionCadence);
  };

  return availableCadences.length > 0 ? (
    <>
      <Combobox label={t('filtersPanelPartitionCadenceLabel')} value={selectedCadence} onHeChange={cadenceChanged}>
        {availableCadences.map((p) => (
          <Option key={`${p.partitionCadence}`} value={`${p.partitionCadence}`} title={`${p.partitionCadence}`}>
            {`${p.partitionCadence}`}
          </Option>
        ))}
      </Combobox>
      <br />
    </>
  ) : null;
});
